
import { ArticleConfig } from '@/common/api/models.pleased';
import { EventType } from '.';
import { MessageType, SystemOptionType } from '../enum';
import { PleasedArticleAction } from '../flow-frameworks/ChatMessageActions.static';
import { ArticleSystemOption, BaseEvent, BaseExternalEvent, BaseMessage, ConnectToAgent, MessageListItem, NoneOfTheAbove, ResolutionConfig, SystemOptionEvent } from '../types';
import { isInteractiveMessage } from './ItemType';

interface MessageListItemLike extends Partial<MessageListItem> { }

export function getInteractivePart (message: BaseMessage, part: 0 | 1, def = '') {
  return isInteractiveMessage(message) ? message.interactiveMessageParts[part] : def;
}

/**
 * Build a message history for the new message.
 * @param event The event to create the message history from.
 * @param historicMessage The message that triggered this event.
 * @param append Any messages to append to the history for information purposes.
 * @returns An array of message history.
 */
export function createMessageHistory (event: BaseEvent | BaseExternalEvent, historicMessage: BaseMessage, ...append: BaseMessage[]) {
  const { history = [] } = historicMessage;
  return [...history, { message: historicMessage, event }, ...append.map(m => ({ message: m }))];
}

/**
 * Similar to `Array.findIndex`, returns index if the message exists, or returns `-1` if does not exist.
 * @param messages List of messages.
 * @param toFind The message object to find.
 */
export function findMessageIndex (messages: MessageListItem[], toFind: MessageListItemLike) {
  return messages.findIndex(m => isMessageEqual(m, toFind));
}

/**
 * Works like `Array.filter` to remove a message from the list of messages.
 * @param messages The messages to delete from.
 * @param toDelete The object to delete.
 * @returns A new list of messages with the message removed.
 */
export function deleteMessage (messages: MessageListItem[], toDelete: MessageListItemLike) {
  return messages.filter(m => !isMessageEqual(m, toDelete));
}

/**
 * Compare two messages to check if they are equal. This follows the specification in {@link MessageListItem#id}.
 * @see MessageListItem#id The specification is implemented here.
 */
export function isMessageEqual (lhs: MessageListItemLike, rhs: MessageListItemLike) {
  return lhs === rhs || (lhs.id && rhs.id && (lhs.id === rhs.id));
}

/**
 * Returns `true` if the message exists.
 * @param messages The messages "haystack"
 * @param toFind The "needle" to find
 */
export function hasMessage (messages: MessageListItem[], toFind: MessageListItemLike) {
  return findMessageIndex(messages, toFind) >= 0;
}

/**
 * Get the selected category from the message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getCategoryOptionFromMessageHistory (history: BaseMessage['history'], defaultValue: object | null = null) {
  return (history?.find(h => EventType.isOptionEvent(h.event) && h.message.type === MessageType.CATEGORIES_LIST)?.event as SystemOptionEvent)?.payload?.selected || defaultValue;
}

/**
 * Get the selected category from the message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getCategoryTitleFromMessageHistory (history: BaseMessage['history'], defaultValue = '') {
  return getCategoryOptionFromMessageHistory(history, {})?.title || defaultValue;
}

/**
 * Get the title of the section that was last selected from message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getSectionTitleFromMessageHistory (history: BaseMessage['history'], defaultValue = '') {
  // Find the last selected section title
  const option: null | SystemOptionEvent = ([...(history || [])].reverse().find(h => EventType.isOptionEvent(h.event) && h.message.type === MessageType.SECTION_LIST)?.event as SystemOptionEvent);
  return option?.payload?.selected?.title || defaultValue;
}

export function buildResolution (source: { showEndMessage?: boolean; endMessage?: string }): ResolutionConfig {
  return {
    show: !!source.showEndMessage,
    message: source.endMessage || '',
  };
}

export function buildConnectConfig (source: {
  connectToAgent?: boolean;
  loginRequired?: boolean;
  showLiveChat?: boolean;
  showOpenRequest?: boolean;
  showVoiceChat?: boolean;
  transactionMessage?: string;
  skipAiChat?: boolean;
  showDescriptionBox?: boolean | null;
}): ConnectToAgent {
  return {
    connect: !!source.connectToAgent,
    loginRequired: !!source.loginRequired,
    liveChat: !!source.showLiveChat,
    openRequest: !!source.showOpenRequest,
    voiceChat: !!source.showVoiceChat,
    transactionMessage: source.transactionMessage,
    skipAiChat: !!source.skipAiChat,
    showDescriptionBox: !!source.showDescriptionBox,
  };
}

export function buildNoneOfTheAboveConfig (source: {
  connectToAgent?: boolean;
  loginRequired?: boolean;
  showLiveChat?: boolean;
  showOpenRequest?: boolean;
  showVoiceChat?: boolean;
  transactionMessage?: string;
  showToEndUser: boolean;
  noneOfTheAbove: boolean;
  responseMessage?: string;
  showEndMessage?: boolean;
  endMessage?: string;
}): NoneOfTheAbove {
  return {
    show: source.noneOfTheAbove && source.showToEndUser,
    showResponseMessage: !!source.responseMessage,
    responseMessage: source.responseMessage,
    config: {
      connect: buildConnectConfig(source),
      resolution: buildResolution(source),
    },
  };
}

export function buildArticleOption (source: ArticleConfig): ArticleSystemOption {
  return {
    kind: SystemOptionType.ARTICLE,
    id: source.id,
    title: source.title,

    hideContent: Boolean(source.hideContent),
    transactionType: source.transactionType || '',
    transactionTypes: source.transactionTypes || [],
    showDescriptionBox: source.showDescriptionBox ?? true,
    descriptionMessage: source.descriptionMessage || '',

    overrideParentConnectAgent: Boolean(source.overrideParentConnectAgent),
    connectToAgent: Boolean(source.connectToAgent),
    loginRequired: Boolean(source.loginRequired),
    showLiveChat: Boolean(source.showLiveChat),
    showOpenRequest: Boolean(source.showOpenRequest),
    showVoiceChat: Boolean(source.showVoiceChat),

    showConnectAgentMessage: Boolean(source.showConnectAgentMessage),
    connectAgentMessage: source.connectAgentMessage || '',

    enableAbTesting: Boolean(source.enableAbTesting),
    abValue: source.abValue || '',
    enableMetadataRules: source.enableMetadataRules || false,
    metadataRules: source.metadataRules || [],

    // we need to keep it boolean | null/undefined to determine if we should use the parent configuration
    skipAiChat: source.skipAiChat,

    triggerTicketCreation: !!source.triggerTicketCreation,
    ticketCreationResponseMessage: source.ticketCreationResponseMessage || '',
    ...(source.checks ? { checks: source.checks } : {}),
    ...(source.checkActionId ? { checkActionId: source.checkActionId } : {}),
    ...(source.action ? { action: { action: source.action, actionMessage: source.actionMessage || '', actionTitle: source.actionText || '' } } : {}),

    category: source.category || '',
    section: source.section || '',
  };
}

export function buildConnectToAgentWithArticleConfig(source: ConnectToAgent, articleConfig: ArticleSystemOption): ConnectToAgent {
  // We need to check for parent skipAiChat configuration if article one not selected
  const skipAiChat = articleConfig.skipAiChat ?? source.skipAiChat;

  if (articleConfig.overrideParentConnectAgent) {
    const articleConnectConfig = buildConnectConfig(articleConfig);

    return {
      ...articleConfig,
      ...articleConnectConfig,
      skipAiChat,
    }
  }

  // If the article does not override the parent connect agent, then use the parent connect agent configuration.
  const { connectToAgent, loginRequired, showLiveChat, showOpenRequest, showVoiceChat, ...restArticleConfig } = articleConfig;

  return {
    ...source,
    ...restArticleConfig,
    skipAiChat,
  };
}

export function mergeConnectConfig (source: ConnectToAgent, override: Partial<ConnectToAgent>): ConnectToAgent {
  return {
    ...source,
    ...override,
  };
}